import { Student, Teacher, SchoolSettings, HomeroomJournalNote } from '../../../types';
import { ClassInventoryItem, ClassRoomFacility, ClassCleanlinessActivity } from './types';

interface PrintReportData {
  homeroomClass: string;
  selectedMonthName: string;
  selectedYear: number;
  schoolSettings: SchoolSettings;
  activeUser: Teacher;
  studentDemographics: {
    total: number;
    laki: number;
    perempuan: number;
    pctLaki: number;
    pctPerempuan: number;
    kipPip: number;
    aktif: number;
  };
  attendanceStats: {
    totalWorkingDays: number;
    totalStudentDays: number;
    sakitCount: number;
    pctSakit: number;
    izinCount: number;
    pctIzin: number;
    alpaCount: number;
    pctAlpa: number;
    terlambatCount: number;
    pctTerlambat: number;
    pulangCount: number;
    pctPulang: number;
    hadirCount: number;
    pctHadir: number;
    totalSIA: number;
    pctSIA: number;
    avgSIAPerStudent: number;
    avgPctSIA: number;
  };
  studentAttendanceRows: Array<{
    index: number;
    student: Student;
    H: number;
    S: number;
    I: number;
    A: number;
    T: number;
    P: number;
    totalWorkingDays: number;
    attendancePercentage: number;
    detailDates: string;
  }>;
  studentProblems: HomeroomJournalNote[];
  inventoryItems: ClassInventoryItem[];
  roomFacilities: ClassRoomFacility[];
  cleanlinessActivities: ClassCleanlinessActivity[];
}

export function generateMonthlyReportPrintHtml(data: PrintReportData): string {
  const {
    homeroomClass,
    selectedMonthName,
    selectedYear,
    schoolSettings,
    activeUser,
    studentDemographics,
    attendanceStats,
    studentAttendanceRows,
    studentProblems,
    inventoryItems,
    roomFacilities,
    cleanlinessActivities
  } = data;

  const schoolTitle = schoolSettings?.schoolName || 'SMP NEGERI 1';
  const deptTitle = schoolSettings?.departmentName || 'DINAS PENDIDIKAN';
  const govTitle = schoolSettings?.governmentName || 'PEMERINTAH KABUPATEN SUBANG';
  const schoolAddress = schoolSettings?.address || 'Jl. Raya Pendidikan No. 1';

  const leftLogoHtml = schoolSettings?.departmentLogoUrl
    ? `<img src="${schoolSettings.departmentLogoUrl}" style="height: 50px; max-width: 60px; object-fit: contain;" alt="Logo Dinas" />`
    : `<img src="https://upload.wikimedia.org/wikipedia/commons/9/9c/Logo_Kementerian_Pendidikan_dan_Kebudayaan.png" style="height: 50px; max-width: 60px; object-fit: contain;" alt="Logo Tut Wuri" />`;

  const rightLogoHtml = schoolSettings?.logoUrl
    ? `<img src="${schoolSettings.logoUrl}" style="height: 50px; max-width: 60px; object-fit: contain;" alt="Logo Sekolah" />`
    : `<img src="https://upload.wikimedia.org/wikipedia/commons/9/9c/Logo_Kementerian_Pendidikan_dan_Kebudayaan.png" style="height: 50px; max-width: 60px; object-fit: contain;" alt="Logo Tut Wuri" />`;

  // Section C: Problems Table HTML
  const problemsRowsHtml =
    studentProblems.length === 0
      ? `<tr><td colspan="6" style="text-align: center; color: #64748b; font-style: italic; padding: 6px;">Nihil (Tidak ada catatan masalah/kejadian peserta didik pada periode ini)</td></tr>`
      : studentProblems
          .map(
            (p, idx) => `
          <tr>
            <td style="text-align: center;">${idx + 1}</td>
            <td style="text-align: center;">${p.date || '-'}</td>
            <td style="font-weight: 600;">${p.studentName || '-'}</td>
            <td style="text-align: center;">${p.category || 'Umum'}</td>
            <td>${p.description || '-'}</td>
            <td>${p.actionTaken || '-'}${p.status ? ` (${p.status})` : ''}</td>
          </tr>
        `
          )
          .join('');

  // Section D: Inventory Table HTML
  const inventoryRowsHtml = inventoryItems
    .map(
      (item, idx) => `
      <tr>
        <td style="text-align: center;">${idx + 1}</td>
        <td style="font-weight: 600;">${item.name}</td>
        <td style="text-align: center; font-weight: bold;">${item.quantity}</td>
        <td style="text-align: center; font-weight: bold; ${
          item.condition === 'Baik' ? 'color: #16a34a;' : item.condition === 'Rusak Ringan' ? 'color: #d97706;' : 'color: #dc2626;'
        }">${item.condition}</td>
        <td>${item.notes || '-'}</td>
      </tr>
    `
    )
    .join('');

  // Section E: Room Facilities Table HTML
  const facilityRowsHtml = roomFacilities
    .map(
      (fac, idx) => `
      <tr>
        <td style="text-align: center;">${idx + 1}</td>
        <td style="font-weight: 600;">${fac.name}</td>
        <td style="text-align: center; font-weight: bold; ${
          fac.condition === 'Baik' ? 'color: #16a34a;' : fac.condition === 'Rusak Ringan' ? 'color: #d97706;' : 'color: #dc2626;'
        }">${fac.condition}</td>
        <td>${fac.notes || '-'}</td>
      </tr>
    `
    )
    .join('');

  // Section F: Cleanliness Activities Table HTML
  const cleanlinessRowsHtml = cleanlinessActivities
    .map(
      (act, idx) => `
      <tr>
        <td style="text-align: center;">${idx + 1}</td>
        <td>${act.activity}</td>
        <td>${act.scheduleOrPerson || '-'}</td>
      </tr>
    `
    )
    .join('');

  // Student Attendance Roster HTML
  const attendanceRosterRowsHtml = studentAttendanceRows
    .map((r) => {
      const g = r.student.gender === 'Laki-laki' ? 'L' : 'P';
      return `
        <tr>
          <td style="text-align: center;">${r.index}</td>
          <td style="text-align: center;">${r.student.nis || r.student.nisn || '-'}</td>
          <td style="font-weight: 600;">${r.student.name}</td>
          <td style="text-align: center;">${g}</td>
          <td style="text-align: center; font-weight: bold;">${r.H}</td>
          <td style="text-align: center; font-weight: bold; color: #0284c7;">${r.S}</td>
          <td style="text-align: center; font-weight: bold; color: #d97706;">${r.I}</td>
          <td style="text-align: center; font-weight: bold; color: #dc2626;">${r.A}</td>
          <td style="text-align: center; font-weight: bold; color: #7c3aed;">${r.T}</td>
          <td style="text-align: center; font-weight: bold; color: #ea580c;">${r.P}</td>
          <td style="text-align: center; font-weight: bold; color: ${r.attendancePercentage >= 85 ? '#16a34a' : '#dc2626'};">${r.attendancePercentage}%</td>
          <td style="font-size: 8pt; color: #475569;">${r.detailDates}</td>
        </tr>
      `;
    })
    .join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Laporan Bulanan Kelas ${homeroomClass} - ${selectedMonthName} ${selectedYear}</title>
      <style>
        @page {
          size: portrait;
          margin: 10mm 12mm 12mm 12mm;
        }
        body {
          font-family: 'Times New Roman', Times, serif;
          font-size: 9.5pt;
          color: #000;
          line-height: 1.3;
          background: #fff;
          margin: 0;
          padding: 8px;
        }
        .kop-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 2.5px solid #000;
          padding-bottom: 6px;
          margin-bottom: 10px;
        }
        .kop-center {
          text-align: center;
          flex: 1;
          padding: 0 8px;
        }
        .kop-gov { font-size: 10.5pt; margin: 0; }
        .kop-dept { font-size: 11.5pt; font-weight: bold; margin: 0; }
        .kop-school { font-size: 13.5pt; font-weight: bold; margin: 1px 0; letter-spacing: 0.5px; }
        .kop-address { font-size: 8pt; font-style: italic; margin: 0; color: #333; }
        .title-section {
          text-align: center;
          margin-bottom: 12px;
        }
        .title-main {
          font-size: 12pt;
          font-weight: bold;
          text-decoration: underline;
          margin: 0;
        }
        .title-sub {
          font-size: 9.5pt;
          margin-top: 2px;
        }
        .meta-table {
          width: 100%;
          margin-bottom: 10px;
          font-size: 9pt;
        }
        .meta-table td {
          padding: 2px 4px;
        }
        .section-heading {
          font-size: 10pt;
          font-weight: bold;
          margin-top: 12px;
          margin-bottom: 5px;
          background-color: #f1f5f9;
          padding: 3px 6px;
          border-left: 3px solid #000;
        }
        table.data-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
          font-size: 8.5pt;
        }
        table.data-table th, table.data-table td {
          border: 1px solid #000;
          padding: 3.5px 3px;
        }
        table.data-table th {
          background-color: #f8fafc;
          text-align: center;
          font-weight: bold;
        }
        .signature-container {
          display: flex;
          justify-content: space-between;
          margin-top: 20px;
          page-break-inside: avoid;
          font-size: 9pt;
        }
        .signature-box {
          text-align: center;
          width: 45%;
        }
        .signature-space {
          height: 55px;
        }
        @media print {
          body { padding: 0; }
          .no-print { display: none !important; }
        }
      </style>
    </head>
    <body>
      <div class="kop-header">
        <div>${leftLogoHtml}</div>
        <div class="kop-center">
          <div class="kop-gov">${govTitle}</div>
          <div class="kop-dept">${deptTitle}</div>
          <div class="kop-school">${schoolTitle}</div>
          <div class="kop-address">${schoolAddress}</div>
        </div>
        <div>${rightLogoHtml}</div>
      </div>

      <div class="title-section">
        <div class="title-main">LAPORAN BULANAN KELAS (WALI KELAS)</div>
        <div class="title-sub">Bulan: ${selectedMonthName} ${selectedYear} | Kelas: ${homeroomClass} | T.A. ${
          schoolSettings?.academicYear || '2025/2026'
        } (${schoolSettings?.semester || 'Genap'})</div>
      </div>

      <table class="meta-table">
        <tr>
          <td style="width: 18%;"><strong>Wali Kelas</strong></td>
          <td style="width: 2%;">:</td>
          <td style="width: 80%;">${activeUser?.name || '-'} (NIP: ${activeUser?.nip || '-'})</td>
        </tr>
      </table>

      <!-- BAGIAN A -->
      <div class="section-heading">A. KEADAAN PESERTA DIDIK (DEMOGRAFI)</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>Kategori Keadaan Siswa</th>
            <th>Laki-laki (L)</th>
            <th>Perempuan (P)</th>
            <th>Jumlah Total</th>
            <th>Penerima KIP / PIP</th>
            <th>Siswa Aktif</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="text-align: left; padding-left: 6px;"><strong>Jumlah Peserta Didik</strong></td>
            <td style="text-align: center; font-weight: bold;">${studentDemographics.laki} Orang (${studentDemographics.pctLaki}%)</td>
            <td style="text-align: center; font-weight: bold;">${studentDemographics.perempuan} Orang (${studentDemographics.pctPerempuan}%)</td>
            <td style="text-align: center; font-weight: bold; background-color: #f1f5f9;">${studentDemographics.total} Siswa</td>
            <td style="text-align: center;">${studentDemographics.kipPip} Siswa</td>
            <td style="text-align: center; font-weight: bold; color: #16a34a;">${studentDemographics.aktif} Siswa</td>
          </tr>
        </tbody>
      </table>

      <!-- BAGIAN B -->
      <div class="section-heading">B. ABSENSI PESERTA DIDIK KELAS ${homeroomClass}</div>
      <table class="data-table">
        <thead>
          <tr>
            <th rowspan="2">Indikator Kehadiran</th>
            <th colspan="2">Sakit (S)</th>
            <th colspan="2">Izin (I)</th>
            <th colspan="2">Alpa (A)</th>
            <th colspan="2">Jumlah Ketidakhadiran (S+I+A)</th>
            <th colspan="2">Rata-rata per Siswa</th>
            <th colspan="2">Kehadiran (H)</th>
          </tr>
          <tr>
            <th>Hari</th>
            <th>%</th>
            <th>Hari</th>
            <th>%</th>
            <th>Hari</th>
            <th>%</th>
            <th>Total Hari</th>
            <th>% Total</th>
            <th>Rata2 Hari</th>
            <th>Rata2 %</th>
            <th>Hari</th>
            <th>% Hadir</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="text-align: left; font-weight: bold; padding-left: 6px;">Akumulasi Bulanan</td>
            <td style="text-align: center; font-weight: bold; color: #0284c7;">${attendanceStats.sakitCount}</td>
            <td style="text-align: center; font-weight: bold; color: #0284c7;">${attendanceStats.pctSakit}%</td>
            <td style="text-align: center; font-weight: bold; color: #d97706;">${attendanceStats.izinCount}</td>
            <td style="text-align: center; font-weight: bold; color: #d97706;">${attendanceStats.pctIzin}%</td>
            <td style="text-align: center; font-weight: bold; color: #dc2626;">${attendanceStats.alpaCount}</td>
            <td style="text-align: center; font-weight: bold; color: #dc2626;">${attendanceStats.pctAlpa}%</td>
            <td style="text-align: center; font-weight: bold; color: #dc2626; background-color: #fef2f2;">${attendanceStats.totalSIA}</td>
            <td style="text-align: center; font-weight: bold; color: #dc2626; background-color: #fef2f2;">${attendanceStats.pctSIA}%</td>
            <td style="text-align: center; font-weight: bold; color: #475569;">${attendanceStats.avgSIAPerStudent} Hari</td>
            <td style="text-align: center; font-weight: bold; color: #475569;">${attendanceStats.avgPctSIA}%</td>
            <td style="text-align: center; font-weight: bold; color: #16a34a; background-color: #f0fdf4;">${attendanceStats.hadirCount}</td>
            <td style="text-align: center; font-weight: bold; color: #16a34a; background-color: #f0fdf4;">${attendanceStats.pctHadir}%</td>
          </tr>
        </tbody>
      </table>

      <!-- BAGIAN C -->
      <div class="section-heading">C. MASALAH SISWA (CATATAN BIMBINGAN & KEJADIAN PESERTA DIDIK)</div>
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 4%;">No</th>
            <th style="width: 12%;">Tanggal</th>
            <th style="width: 22%;">Nama Peserta Didik</th>
            <th style="width: 14%;">Kategori</th>
            <th style="width: 28%;">Deskripsi Kejadian / Masalah</th>
            <th style="width: 20%;">Tindak Lanjut / Penanganan</th>
          </tr>
        </thead>
        <tbody>
          ${problemsRowsHtml}
        </tbody>
      </table>

      <!-- BAGIAN D -->
      <div class="section-heading">D. KEADAAN BARANG INVENTARIS KELAS</div>
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 4%;">No</th>
            <th style="width: 32%;">Nama Barang Inventaris</th>
            <th style="width: 10%;">Jumlah</th>
            <th style="width: 18%;">Kondisi</th>
            <th style="width: 36%;">Keterangan</th>
          </tr>
        </thead>
        <tbody>
          ${inventoryRowsHtml}
        </tbody>
      </table>

      <!-- BAGIAN E -->
      <div class="section-heading">E. KEADAAN RUANG KELAS</div>
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 4%;">No</th>
            <th style="width: 36%;">Nama Elemen / Sarana Ruangan</th>
            <th style="width: 20%;">Kondisi</th>
            <th style="width: 40%;">Keterangan</th>
          </tr>
        </thead>
        <tbody>
          ${facilityRowsHtml}
        </tbody>
      </table>

      <!-- BAGIAN F -->
      <div class="section-heading">F. KEBERSIHAN DAN KEINDAHAN KELAS</div>
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 4%;">No</th>
            <th style="width: 60%;">Aktivitas Yang Dilakukan Untuk Memelihara Kebersihan dan Keindahan Ruangan Kelas</th>
            <th style="width: 36%;">Keterangan / Jadwal / Penanggung Jawab</th>
          </tr>
        </thead>
        <tbody>
          ${cleanlinessRowsHtml}
        </tbody>
      </table>

      <div class="signature-container">
        <div class="signature-box">
          <div>Mengetahui,</div>
          <div>Kepala Sekolah</div>
          <div class="signature-space"></div>
          <div><strong>${schoolSettings?.principalName || 'Kepala Sekolah'}</strong></div>
          <div>NIP. ${schoolSettings?.principalNip || '-'}</div>
        </div>
        <div class="signature-box">
          <div>${schoolSettings?.kabupaten || 'Subang'}, ${new Date().getDate()} ${selectedMonthName} ${selectedYear}</div>
          <div>Wali Kelas ${homeroomClass}</div>
          <div class="signature-space"></div>
          <div><strong>${activeUser?.name || 'Wali Kelas'}</strong></div>
          <div>NIP. ${activeUser?.nip || '-'}</div>
        </div>
      </div>

      <script>
        window.onload = function() {
          window.print();
        };
      </script>
    </body>
    </html>
  `;
}
